/**
 * OFFERING A CALLER'S JOB TO ONE OF OUR STAFF (NYIA, 2026-10-21).
 *
 * Neelum: Laura fills in when and where, taps a staff member, and they get a
 * text asking whether they can take it. Their button goes orange while she
 * waits and green when they say yes. Staff have no Shop Desk login (9:00
 * AM: "if it can just text them the details"), so the text is the whole job
 * as far as they are concerned, and their reply is read straight off the line.
 *
 * Pure, and in src/domain, because the browser shows the text before it goes
 * and the server sends it, and two copies of the wording drift.
 * 0231_a_job_is_offered_to_our_staff.sql
 */

export type Logistics = { job_date: string; job_time: string; location: string; details: string };

export const NO_LOGISTICS: Logistics = { job_date: "", job_time: "", location: "", details: "" };

/** "2026-10-28" reads "Tue, Oct 28". Built by hand so the server and a phone agree. */
export function dayOf(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return "";
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()];
  return `${day}, ${mon} ${d.getUTCDate()}`;
}

/** "14:30" reads "2:30 PM". */
export function timeOf(hm: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hm);
  if (!m) return "";
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

/** When, in one line: "Tue, Oct 28 at 2:30 PM", or whichever half is there. */
export function whenOf(l: Logistics): string {
  const d = dayOf(l.job_date), t = timeOf(l.job_time);
  return d && t ? `${d} at ${t}` : d || t;
}

/** What is missing before a job can be offered. Null when it is ready. */
export function notReady(l: Logistics): string | null {
  if (!dayOf(l.job_date)) return "Add the date of the job first.";
  if (!l.location.trim()) return "Add the location of the job first.";
  return null;
}

/**
 * THE TEXT A STAFF MEMBER GETS. No client name or number: the details box
 * is where Laura puts what they need, and nothing else leaves the card.
 */
export function offerText(o: { firm: string; staffName: string; l: Logistics }): string {
  const first = o.staffName.trim().split(/\s+/)[0] ?? "";
  const lines = [
    `Hi ${first}, ${o.firm.trim() || "the office"} has a job for you.`,
    `When: ${whenOf(o.l)}`,
    `Where: ${o.l.location.trim()}`,
    ...(o.l.details.trim() ? [`Details: ${o.l.details.trim()}`] : []),
    "Can you take it? Reply YES or NO.",
  ];
  return lines.join("\n").slice(0, 1200);
}

/**
 * THEIR REPLY, read as a yes, a no, or neither. Only the start of the text
 * counts, so "yes but I need the address" is a yes and a question about the
 * job is neither, and the desk keeps waiting. "No problem" is a yes.
 */
export function yesOrNo(body: string | null | undefined): "yes" | "no" | null {
  const s = String(body ?? "").toLowerCase().replace(/[‘’]/g, "'").replace(/^[\s"'.,!]+/, "");
  if (!s) return null;
  if (/^no (problem|prob|worries)\b/.test(s)) return "yes";
  /* Before the yeses, or "I can't" reads as "I can". */
  if (/^(i )?(can't|cant|cannot)\b/.test(s)) return "no";
  if (/^(yes|yess+|yep|yup|yeah|yea|ya|y|sure|ok|okay|k|absolutely|definitely|confirmed?|i can|i'll take it|i will take it|count me in|on it|👍)(\b|$|[\s.,!])/u.test(s)) return "yes";
  if (/^(no|nope|nah|n|can't|cant|cannot|i can't|i cant|i cannot|sorry|unavailable|not available|pass)(\b|$|[\s.,!])/.test(s)) return "no";
  return null;
}

export type Ask = { id: string; staff_id: string; asked_at: string; answer: "yes" | "no" | null };
export type Reply = { at: string; body: string | null };

/**
 * WHICH ASK A REPLY ANSWERS. A staff member may be asked about two jobs in
 * a row, so a reply belongs to the newest ask sent to them before it, and
 * only a reply after that ask counts. Returns ask id -> answer and when.
 * `asks` is every live ask to this one staff member; `replies` their texts in.
 */
export function answersFrom(asks: Ask[], replies: Reply[]): Map<string, { answer: "yes" | "no"; at: string }> {
  const out = new Map<string, { answer: "yes" | "no"; at: string }>();
  const ordered = [...asks].sort((a, b) => a.asked_at.localeCompare(b.asked_at));
  for (const r of [...replies].sort((a, b) => a.at.localeCompare(b.at))) {
    const said = yesOrNo(r.body);
    if (!said) continue;
    const ask = ordered.filter((a) => a.asked_at < r.at).pop();
    if (!ask || ask.answer || out.has(ask.id)) continue;
    out.set(ask.id, { answer: said, at: r.at });
  }
  return out;
}
